import { useLayoutEffect, useRef } from "react";
import { isBrowserOffline, reloadForStaleBundle } from "./staleBundle";

/**
 * The production build writes its id into `index.html` and into
 * `build-info.json` next to it (see the frontend's `vite-build-info.ts`). A
 * tab compares the two to notice a deploy: after one, its next page change is
 * a full page load to the target URL instead of a client-side navigation, so
 * the user gets the new bundle without a broken page and without a reload
 * under an open form.
 */

/** The `<meta>` tag with the build id; its `data-build-info-url` names the file. */
export const BUILD_ID_META_NAME = "atoms-build-id";

/** How often a visible tab asks for the current build. */
export const BUILD_UPDATE_CHECK_INTERVAL_MS = 5 * 60_000;

/** No check closer than this after the last, whatever triggers it. */
const MIN_CHECK_GAP_MS = 30_000;

export type RunningBuild = Readonly<{ buildId: string; buildInfoUrl: string }>;

/**
 * The build this page runs; `undefined` without the tag (dev server, tests).
 * The file's URL is resolved now, against the URL the page was loaded from,
 * as the browser resolved the bundle: a relative Vite `base` then still
 * points at the build after the router changed the URL.
 */
export function readRunningBuild(
  doc:
    | Pick<Document, "querySelector" | "baseURI">
    | undefined = globalThis.document,
): RunningBuild | undefined {
  const meta = doc?.querySelector<HTMLMetaElement>(
    `meta[name="${BUILD_ID_META_NAME}"]`,
  );
  const buildId = meta?.content;
  const rawUrl = meta?.dataset.buildInfoUrl;
  if (!doc || !buildId || !rawUrl) return undefined;
  return { buildId, buildInfoUrl: new URL(rawUrl, doc.baseURI).href };
}

let outdated = false;

/** Whether a newer build than this page's has been deployed. */
export function isBuildOutdated(): boolean {
  return outdated;
}

/** Test helper: back to the state of a fresh page. */
export function resetBuildUpdateForTests(): void {
  outdated = false;
}

/**
 * Asks the server for the current build id. Returns `true` when it differs
 * from the running one; a failed request says nothing about the deploy.
 */
export async function checkForBuildUpdate(
  running: RunningBuild,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<boolean> {
  if (outdated) return true;
  try {
    const response = await fetchImpl(running.buildInfoUrl, {
      cache: "no-store",
    });
    if (!response.ok) return false;
    const info: unknown = await response.json();
    const buildId =
      typeof info === "object" && info !== null && "buildId" in info
        ? info.buildId
        : undefined;
    if (typeof buildId === "string" && buildId !== running.buildId) {
      outdated = true;
    }
  } catch {
    // Offline or a proxy error page: try again at the next occasion.
  }
  return outdated;
}

export type BuildUpdateWatcherOptions = Readonly<{
  running?: RunningBuild;
  fetchImpl?: typeof fetch;
  intervalMs?: number;
}>;

/**
 * Checks for a new build while the tab is visible: every
 * {@link BUILD_UPDATE_CHECK_INTERVAL_MS}, when the tab becomes visible and
 * when the browser comes back online. Stops once a new build is seen. Does
 * nothing without a build id (dev server). Returns the cleanup.
 */
export function startBuildUpdateWatcher(
  options: BuildUpdateWatcherOptions = {},
): () => void {
  const running = options.running ?? readRunningBuild();
  if (!running) return () => undefined;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const intervalMs = options.intervalMs ?? BUILD_UPDATE_CHECK_INTERVAL_MS;

  let lastCheckAt = Number.NEGATIVE_INFINITY;
  let stopped = false;
  const check = () => {
    if (stopped || outdated || document.visibilityState !== "visible") return;
    const now = Date.now();
    if (now - lastCheckAt < MIN_CHECK_GAP_MS) return;
    lastCheckAt = now;
    void checkForBuildUpdate(running, fetchImpl).then((isOutdated) => {
      if (isOutdated) stop();
    });
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") check();
  };

  const interval = setInterval(check, intervalMs);
  document.addEventListener("visibilitychange", onVisibilityChange);
  globalThis.addEventListener("online", check);

  function stop() {
    stopped = true;
    clearInterval(interval);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    globalThis.removeEventListener("online", check);
  }
  return stop;
}

/**
 * Turns the first page change after a deploy into a full page load: the
 * router has already switched the URL, so the reload lands on the target
 * page with the new bundle. A change of only the query or hash (filters,
 * tabs) stays client-side, and so does any change while the browser is
 * offline. Mount once inside the router and pass the
 * router's current pathname (the caller owns the router instance).
 */
export function useReloadOnNavigationWhenOutdated(
  pathname: string,
  reload: () => boolean = reloadForStaleBundle,
): void {
  const previousPathname = useRef(pathname);

  // Layout effect: the reload starts before the new page paints.
  useLayoutEffect(() => {
    if (previousPathname.current === pathname) return;
    previousPathname.current = pathname;
    // Offline, a reload would replace the working app with an error page:
    // the next page change online loads the new build.
    if (isBuildOutdated() && !isBrowserOffline()) reload();
  }, [pathname, reload]);
}
